import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { io, Socket } from 'socket.io-client'
import { useIsAuthenticated, useUser } from '@/app/store/auth'
import { getAccessToken } from '@/features/ai-chat/core/utils/authHeaders'

/**
 * Presence STATUS, distinct from presence.
 *
 * Whether someone is connected is a fact the socket already knows. What they are doing is not —
 * a locked screen still holds an open socket, and showing that person as available is how a
 * colleague rings someone who walked away ten minutes ago.
 *
 * 'offline' is the absence of a connection and is decided by user-online/user-offline; the server
 * never sends it as a status. It appears here only as what getUserStatus returns for someone who
 * is not in onlineUsers, so callers have one function to ask rather than two.
 */
export type PresenceStatus = 'available' | 'busy' | 'away'
export type PresenceState = PresenceStatus | 'offline'

interface PresenceContextType {
    onlineUsers: Set<number>
    isUserOnline: (userId: number) => boolean
    /** 'offline' when not connected, otherwise whatever their most available tab reports. */
    getUserStatus: (userId: number) => PresenceState
    /** Declares what THIS tab is doing. The server takes the most available across a user's tabs. */
    setMyStatus: (status: PresenceStatus) => void
    socket: Socket | null
    isConnected: boolean
    connectionError: string | null
    retryConnection: () => void
}

const PresenceContext = createContext<PresenceContextType>({
    onlineUsers: new Set(),
    isUserOnline: () => false,
    getUserStatus: () => 'offline',
    setMyStatus: () => {},
    socket: null,
    isConnected: false,
    connectionError: null,
    retryConnection: () => {},
})

// Handshakes fail in bursts (a task replacement, a laptop lid). Only say so once socket.io
// has failed this many times in a row, so a one-second blip does not throw a banner over the
// screen an engineer is working on.
const VISIBLE_FAILURE_THRESHOLD = 3

export function PresenceProvider({ children }: { children: ReactNode }) {
    const isAuthenticated = useIsAuthenticated()
    const user = useUser()
    const [onlineUsers, setOnlineUsers] = useState<Set<number>>(new Set())
    const [statuses, setStatuses] = useState<Map<number, PresenceStatus>>(new Map())
    /**
     * What this tab last declared. Kept so it can be re-sent on reconnect: the server holds
     * status per socket, so a dropped connection silently resets an away user to available.
     */
    const myStatusRef = useRef<PresenceStatus>('available')
    // The socket is handed to consumers as STATE, not out of the ref. This layer now carries
    // the app-wide call signalling: handing it out of a ref meant VideoCallProvider saw null
    // until the provider happened to re-render for some other reason, and `call-incoming` was
    // never bound on the renders in between.
    const [socket, setSocket] = useState<Socket | null>(null)
    const [isConnected, setIsConnected] = useState(false)
    const [connectionError, setConnectionError] = useState<string | null>(null)
    const socketRef = useRef<Socket | null>(null)
    const failedAttemptsRef = useRef(0)

    useEffect(() => {
        if (!isAuthenticated || !user?.id) {
            // Disconnect if logged out
            if (socketRef.current) {
                socketRef.current.disconnect()
                socketRef.current = null
            }
            setSocket(null)
            setIsConnected(false)
            setConnectionError(null)
            setOnlineUsers(new Set())
            return
        }

        // Connect to the default namespace for presence
        // In dev: Vite proxies /socket.io to backend, so use same origin
        // In production: VITE_API_BASE_URL points to the backend (ALB/API Gateway)
        const apiBase = import.meta.env.VITE_API_BASE_URL
        const socketBaseUrl = apiBase || window.location.origin

        const socket = io(socketBaseUrl, {
            path: '/socket.io',
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 2000,
            // Never stop trying. This socket carries presence, the global unread badge AND
            // every incoming call: giving up after five attempts (~30s, i.e. one ECS task
            // replacement) meant nobody could ring this engineer again until they happened to
            // reload the page, with nothing on screen to say so.
            reconnectionDelayMax: 20000,
            reconnectionAttempts: Infinity,
            // Authenticate the socket with the JWT; function form re-reads the token on
            // every (re)connect so it picks up refreshed tokens. The server derives the
            // user from this token and ignores any client-supplied id.
            auth: (cb) => cb({ token: getAccessToken() }),
        })

        socketRef.current = socket
        setSocket(socket)

        socket.on('connect', () => {
            failedAttemptsRef.current = 0
            setIsConnected(true)
            setConnectionError(null)
            socket.emit('identify-user', { userId: user.id })
            socket.emit('request-online-users')
            // The server holds status per SOCKET, so a reconnect starts this tab at available.
            // Re-declaring is what stops a locked screen quietly reporting itself as back.
            if (myStatusRef.current !== 'available') {
                socket.emit('set-presence-status', { status: myStatusRef.current })
            }
        })

        socket.on('disconnect', () => {
            setIsConnected(false)
        })

        // A handshake refused by the server's auth middleware (expired token) sets
        // socket.active false and socket.io will NEVER retry on its own — that case has to be
        // reported immediately, with a retry the engineer can press.
        socket.on('connect_error', (error: Error) => {
            failedAttemptsRef.current += 1
            setIsConnected(false)
            if (!socket.active || failedAttemptsRef.current >= VISIBLE_FAILURE_THRESHOLD) {
                setConnectionError(error?.message || 'connect_error')
            }
        })

        socket.on('online-users-list', (data: { userIds: number[]; statuses?: Record<string, PresenceStatus> }) => {
            setOnlineUsers(new Set(data.userIds || []))
            if (data.statuses) {
                setStatuses(new Map(Object.entries(data.statuses).map(([id, st]) => [Number(id), st])))
            }
        })

        socket.on('user-status', (data: { userId: number; status: PresenceStatus }) => {
            setStatuses(prev => new Map(prev).set(data.userId, data.status))
        })

        socket.on('user-online', (data: { userId: number }) => {
            setOnlineUsers(prev => new Set([...prev, data.userId]))
        })

        socket.on('user-offline', (data: { userId: number }) => {
            setOnlineUsers(prev => {
                const next = new Set(prev)
                next.delete(data.userId)
                return next
            })
            // Their last status is meaningless once they are gone, and leaving it behind would
            // show a stale "away" against someone who has closed the laptop entirely.
            setStatuses(prev => {
                const next = new Map(prev)
                next.delete(data.userId)
                return next
            })
        })

        return () => {
            socket.disconnect()
            socketRef.current = null
            setSocket(null)
            setIsConnected(false)
        }
    }, [isAuthenticated, user?.id])

    // Reconnect by hand. The auth callback re-reads the access token, so a refreshed one is
    // picked up here — this is the only way back from a handshake the server refused.
    const retryConnection = useCallback(() => {
        failedAttemptsRef.current = 0
        setConnectionError(null)
        const current = socketRef.current
        if (!current || current.connected) return
        current.connect()
    }, [])

    const isUserOnline = useCallback((userId: number) => {
        return onlineUsers.has(userId)
    }, [onlineUsers])

    const getUserStatus = useCallback((userId: number): PresenceState => {
        if (!onlineUsers.has(userId)) return 'offline'
        return statuses.get(userId) ?? 'available'
    }, [onlineUsers, statuses])

    const setMyStatus = useCallback((status: PresenceStatus) => {
        myStatusRef.current = status
        socketRef.current?.emit('set-presence-status', { status })
    }, [])

    return (
        <PresenceContext.Provider value={{ onlineUsers, isUserOnline, getUserStatus, setMyStatus, socket, isConnected, connectionError, retryConnection }}>
            {children}
            {/* No banner here. Connection state is surfaced by the Topbar indicator
                (components/layout/Topbar.tsx), which is where the user's other status
                affordances live. A full-width red bar across the bottom of every page was
                accurate but far louder than the problem: on a mobile link a sub-second blip
                trips engine.io's heartbeat, and a red alert during a customer demonstration
                costs more than the information is worth. The state below is still exported,
                so nothing about the diagnosis is lost — only the shouting. */}
        </PresenceContext.Provider>
    )
}

export function usePresence() {
    return useContext(PresenceContext)
}

export function useIsOnline(userId: number | null | undefined) {
    const { isUserOnline } = usePresence()
    if (!userId) return false
    return isUserOnline(userId)
}
